import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/*
  Multi-select filter, built on a Radix dropdown menu with checkbox items.

  Why a menu rather than a listbox: the items are independent toggles that each take effect
  immediately, which is menuitemcheckbox semantics, not option semantics. Radix gives arrow
  keys, typeahead, space and enter to toggle, escape to close, and focus return to the
  trigger. Critically it does NOT close on select for checkbox items, which is what makes
  selecting three states in one visit possible from the keyboard.

  The trigger reports the count of active filters in its accessible name, so a keyboard user
  who has just tabbed onto it hears what is already applied rather than only "Status".
*/

export interface FilterOption {
  readonly value: string;
  readonly label: string;
  readonly hint?: string | undefined;
}

export interface FilterMenuProps {
  readonly label: string;
  readonly options: readonly FilterOption[];
  readonly selected: readonly string[];
  readonly onChange: (selected: readonly string[]) => void;
  readonly className?: string;
}

export function FilterMenu({
  label,
  options,
  selected,
  onChange,
  className,
}: FilterMenuProps): React.JSX.Element {
  const count = selected.length;
  const summary =
    count === 0 ? `${label}: any` : `${label}: ${String(count)} selected`;

  function toggle(value: string): void {
    onChange(
      selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value],
    );
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className={cn(
          'inline-flex h-9 items-center gap-1.5 rounded-[var(--radius-control)] border px-3 text-sm',
          'transition-colors',
          count === 0
            ? 'border-border bg-surface text-fg-muted hover:border-border-strong hover:text-fg'
            : 'border-accent-line bg-accent-soft text-accent-soft-fg',
          className,
        )}
        aria-label={summary}
      >
        <span className="font-medium">{label}</span>
        {count > 0 ? (
          <span
            className="rounded-full bg-accent px-1.5 text-[11px] font-semibold text-accent-fg"
            aria-hidden="true"
          >
            {count}
          </span>
        ) : null}
        <ChevronDown className="size-3.5 opacity-70" aria-hidden="true" />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="animate-pop-in z-50 max-h-80 min-w-56 overflow-y-auto rounded-[var(--radius-control)] border border-border bg-surface-raised p-1 shadow-lg"
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold tracking-wide text-fg-subtle uppercase">
            {label}
          </DropdownMenu.Label>
          {options.map((option) => (
            <DropdownMenu.CheckboxItem
              key={option.value}
              checked={selected.includes(option.value)}
              onCheckedChange={() => {
                toggle(option.value);
              }}
              // Keeping the menu open is the whole point: picking three statuses should be
              // three keystrokes, not three round trips through the trigger.
              onSelect={(event) => {
                event.preventDefault();
              }}
              className={cn(
                'relative flex cursor-default items-start gap-2 rounded px-2 py-1.5 pl-7 text-sm text-fg select-none',
                'data-highlighted:bg-accent-soft data-highlighted:text-accent-soft-fg data-highlighted:outline-none',
              )}
            >
              <DropdownMenu.ItemIndicator className="absolute top-2 left-2">
                <Check className="size-3.5" aria-hidden="true" />
              </DropdownMenu.ItemIndicator>
              <span className="min-w-0">
                <span className="block truncate">{option.label}</span>
                {option.hint !== undefined ? (
                  <span className="block text-xs text-fg-subtle">{option.hint}</span>
                ) : null}
              </span>
            </DropdownMenu.CheckboxItem>
          ))}
          {count > 0 ? (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                onSelect={() => {
                  onChange([]);
                }}
                className="flex cursor-default items-center gap-2 rounded px-2 py-1.5 text-sm text-fg-muted select-none data-highlighted:bg-surface-hover data-highlighted:text-fg data-highlighted:outline-none"
              >
                <X className="size-3.5" aria-hidden="true" />
                Clear {label.toLowerCase()}
              </DropdownMenu.Item>
            </>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
